import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CameraIcon, MapPinIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useClockInMutation, useClockOutMutation } from '../services/attendanceApi';
import { useGetMyCompanyQuery } from '../services/companyApi';
import toast from 'react-hot-toast';

const AttendanceCamera = ({ isOpen, onClose, mode = 'clockIn', shiftId, onSuccess }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  const [clockIn] = useClockInMutation();
  const [clockOut] = useClockOutMutation();

  // Get company settings to check attendance type
  const { data: companyData } = useGetMyCompanyQuery();
  const attendanceType = companyData?.data?.settings?.attendance?.type || 'system';
  const selfieRequired = attendanceType === 'selfie';

  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);

  // Get user's current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = new Error('Geolocation is not supported by this browser');
        setLocationError(error.message);
        reject(error);
        return;
      }

      setIsGettingLocation(true);
      setLocationError(null);
      
      console.log('Getting current location...');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          };
          console.log('Location obtained:', locationData);
          setLocation(locationData);
          setLocationError(null);
          setIsGettingLocation(false);
          resolve(locationData);
        },
        (error) => {
          console.error('Location error:', error);
          let errorMessage = 'Unable to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your internet connection.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'Unable to get location. Please try again.';
              break;
          }
          setLocationError(errorMessage);
          setIsGettingLocation(false);
          toast.error(errorMessage);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout
          maximumAge: 0 // 5 minutes
        }
      );
    });
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError(null);
      console.log('Starting camera...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Use front camera for selfie
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        console.log('Camera started successfully');
      }
    } catch (error) {
      console.error('Camera error:', error);
      let errorMessage = 'Unable to access camera';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please enable camera permissions and refresh the page.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device. Please use a device with a camera.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      }
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Capture selfie
  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error('Camera not ready. Please wait and try again.');
      return;
    }

    setIsCapturing(true);
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Ensure video is ready
      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        toast.error('Camera not ready. Please wait for the camera to load.');
        setIsCapturing(false);
        return;
      }

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to base64
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      console.log('Selfie captured successfully');
    } catch (error) {
      console.error('Error capturing selfie:', error);
      toast.error('Failed to capture selfie. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };

  // Handle clock in/out
  const handleAttendance = async () => {
    // Validate requirements based on attendance type
    if (!location) {
      toast.error('Please allow location access');
      return;
    }

    if (selfieRequired && !capturedImage) {
      toast.error('Please capture a selfie for attendance');
      return;
    }

    if (!user || !user._id) {
      toast.error(JSON.stringify(user));
      return;
    }

    try {
      console.log('Starting attendance process...');
      console.log('User:', user);
      console.log('Location:', location);
      console.log('Selfie required:', selfieRequired);
      console.log('Captured image:', !!capturedImage);

      // Use the most appropriate employee identifier
      let employeeId = user.employeeId || user._id || user.id;
      
      if (!employeeId) {
        toast.error('Unable to identify employee. Please contact support.');
        return;
      }

      const attendanceData = {
        employeeId: employeeId,
        shiftId: shiftId,
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        }
      };

      // Only include selfie if required and captured
      if (selfieRequired && capturedImage) {
        attendanceData.selfie = capturedImage;
        console.log('Including selfie data in request');
      }

      console.log('Sending attendance data:', {
        ...attendanceData,
        selfie: attendanceData.selfie ? '[BASE64_IMAGE_DATA]' : undefined
      });

      if (mode === 'clockIn') {
        const result = await clockIn(attendanceData).unwrap();
        console.log('Clock-in successful:', result);
        toast.success(selfieRequired ? 'Clocked in successfully with selfie and location!' : 'Clocked in successfully!');
      } else {
        const result = await clockOut(attendanceData).unwrap();
        console.log('Clock-out successful:', result);
        toast.success(selfieRequired ? 'Clocked out successfully with selfie and location!' : 'Clocked out successfully!');
      }

      // Call onSuccess callback if provided to refresh parent data
      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Attendance error:', error);
      console.error('Error details:', {
        message: error?.message,
        data: error?.data,
        status: error?.status
      });
      
      let errorMessage = `Failed to ${mode}`;
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  // Initialize camera and location on mount
  useEffect(() => {
    if (isOpen) {
      startCamera();
      getCurrentLocation().catch(() => {
        // Location error handled in state
      });
    }

    return () => {
      stopCamera();
    };
  }, [isOpen]);

  // Reset captured image when mode changes or component opens
  useEffect(() => {
    if (isOpen) {
      // Reset all capture-related states when mode changes or modal opens
      setCapturedImage(null);
      setIsCapturing(false);
      setCameraError(null);
      // Don't reset location as it's shared between clock-in and clock-out
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-6 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-display font-semibold text-gray-900 flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    mode === 'clockIn' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {mode === 'clockIn' ? 'CLOCK IN' : 'CLOCK OUT'}
                  </span>
                  {selfieRequired ? 'Take Selfie & Location' : 'Capture Location'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {mode === 'clockIn' 
                    ? 'Take a selfie to mark your arrival' 
                    : 'Take a new selfie to mark your departure'
                  }
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Location Status */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <MapPinIcon className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="font-medium text-gray-700">Location</span>
                  </div>
                  {isGettingLocation ? (
                    <div className="flex items-center text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Getting location...
                    </div>
                  ) : location ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircleIcon className="h-5 w-5 mr-1" />
                      Location captured
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      Location failed
                    </div>
                  )}
                </div>

                {location && (
                  <div className="text-sm text-gray-600">
                    <p>Latitude: {location.latitude.toFixed(6)}</p>
                    <p>Longitude: {location.longitude.toFixed(6)}</p>
                    <p>Accuracy: ±{Math.round(location.accuracy)} meters</p>
                  </div>
                )}

                {locationError && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                    {locationError}
                  </div>
                )}

                {!location && !isGettingLocation && !locationError && (
                  <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded-lg">
                    Location validation will be performed when you clock in/out
                  </div>
                )}

                {!location && !isGettingLocation && (
                  <button
                    onClick={getCurrentLocation}
                    className="text-sm bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Retry Location
                  </button>
                )}
              </div>

              {/* Camera Section - Only show if selfie is required */}
              {selfieRequired && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <CameraIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="font-medium text-gray-700">Selfie</span>
                    </div>
                    {capturedImage ? (
                      <div className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Selfie captured
                      </div>
                    ) : (
                      <div className="flex items-center text-orange-600">
                        <XCircleIcon className="h-5 w-5 mr-1" />
                        Selfie required
                      </div>
                    )}
                  </div>

                <div className="relative">
                  {!capturedImage ? (
                    <div className="relative">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-64 bg-gray-200 rounded-lg object-cover"
                      />
                      {cameraError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
                          <div className="text-center text-red-600">
                            <CameraIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">{cameraError}</p>
                          </div>
                        </div>
                      )}
                      <canvas ref={canvasRef} className="hidden" />
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={capturedImage}
                        alt="Captured selfie"
                        className="w-full h-64 bg-gray-200 rounded-lg object-cover"
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          onClick={() => setCapturedImage(null)}
                          className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {!capturedImage && !cameraError && (
                  <div className="mt-3 flex justify-center">
                    <button
                      onClick={captureSelfie}
                      disabled={isCapturing}
                      className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      {isCapturing ? 'Capturing...' : `Take ${mode === 'clockIn' ? 'Clock In' : 'Clock Out'} Selfie`}
                    </button>
                  </div>
                )}
              </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAttendance}
                  disabled={!location || (selfieRequired && !capturedImage)}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mode === 'clockIn' ? 'Clock In' : 'Clock Out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceCamera;
